import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import type {
  QueryAuditRecommendationsRequestBodyType,
  QueryAuditRecommendationsResponseBodyType,
  SaveAuditRecommendationsFromConversationRequestBodyType,
  SaveAuditRecommendationsFromConversationResponseBodyType,
  SetAuditRecommendationStateRequestBodyType,
  SetAuditRecommendationStateResponseBodyType,
} from "@intelligems/ig-types/functions/opportunity-audit/records/recommendations";

import { aiApiClient } from "@src/api/ai-api";

import { api } from "../api";

export const queryAuditRecommendations = async (
  body: QueryAuditRecommendationsRequestBodyType
): Promise<QueryAuditRecommendationsResponseBodyType> => {
  const resp = await api.post<
    QueryAuditRecommendationsResponseBodyType,
    QueryAuditRecommendationsRequestBodyType
  >("/v2/opportunity-audit/recommendations/query", body);
  return resp.data;
};

/** Sets a recommendation's state, and answers with its new status. */
export const setRecommendationState = async (
  body: SetAuditRecommendationStateRequestBodyType
): Promise<SetAuditRecommendationStateResponseBodyType> => {
  const resp = await api.post<
    SetAuditRecommendationStateResponseBodyType,
    SetAuditRecommendationStateRequestBodyType
  >("/v2/opportunity-audit/recommendations/state", body);
  return resp.data;
};

/**
 * Asks web/ai to save recommendations this conversation showed that
 * node-backend has no row for. web/ai reads them from the conversation; the
 * browser never sends one.
 */
export const saveRecommendationsFromConversation = async (
  body: SaveAuditRecommendationsFromConversationRequestBodyType
): Promise<SaveAuditRecommendationsFromConversationResponseBodyType> => {
  const { data } =
    await aiApiClient.post<SaveAuditRecommendationsFromConversationResponseBodyType>(
      "/opportunity-audit/recommendations/save-from-conversation",
      body
    );
  return data;
};

export const favoriteRecommendationsKey = [
  "opportunity-audit",
  "saved-recommendations",
] as const;

const PAGE = 20;

/**
 * The store's saved recommendations, newest saved first. Every page is read
 * in the background, as the experiences list is, so the table scrolls through
 * all of them.
 */
export const useFavoriteRecommendations = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const saved = useInfiniteQuery({
    queryKey: favoriteRecommendationsKey,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      queryAuditRecommendations({
        state: "favorite",
        limit: PAGE,
        withBody: true,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (last) => last.next ?? undefined,
    select: (data) => ({
      recommendations: data.pages.flatMap((page) => page.recommendations),
    }),
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = saved;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    ...saved,
    isFetchingAllPages: hasNextPage || isFetchingNextPage,
  };
};
